import { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import axios from "../../config/axiosInstance";
import { CLIENTS, CRM } from "../../config/apiConfig";
import { formatNumber, PRIORITY_OPTIONS } from "../crm/crmHelpers";

/*
  Ações em massa da tela de Clientes.

  Aparece só com conta selecionada, para não ocupar a tela de quem só está consultando.
  Cada ação pede confirmação com o número de contas, porque não existe "desfazer" para
  trocar o responsável de 200 contas. Tudo fica no histórico de cada uma.
*/

const fieldClass =
  "rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-pink-300";

const tabClass = (active) =>
  `rounded-lg px-2.5 py-1.5 text-xs font-semibold transition ${
    active
      ? "bg-pink-600 text-white"
      : "border border-slate-200 bg-white text-slate-700 hover:border-pink-200"
  }`;

const accounts = (total) =>
  total === 1 ? "1 conta" : `${formatNumber(total)} contas`;

const BulkActionsBar = ({ ids, isAdmin, owners, tags, onClear, onDone, onError }) => {
  const queryClient = useQueryClient();
  const [panel, setPanel] = useState(null);
  const [ownerUserId, setOwnerUserId] = useState("");
  const [tagName, setTagName] = useState("");
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDueDate, setTaskDueDate] = useState("");
  const [taskPriority, setTaskPriority] = useState("NORMAL");
  const [taskAssignee, setTaskAssignee] = useState("");

  const total = ids.length;
  const typedTag = tagName.trim();
  const exactTag = tags.find(
    (tag) => tag.name.toLowerCase() === typedTag.toLowerCase()
  );

  const afterAction = (message) => {
    queryClient.invalidateQueries("companies");
    queryClient.invalidateQueries(["crm"]);
    setPanel(null);
    setTagName("");
    setTaskTitle("");
    setTaskDueDate("");
    setTaskPriority("NORMAL");
    setTaskAssignee("");
    onDone(message);
  };

  const fail = (error) =>
    onError(error.response?.data?.message || "Não foi possível concluir a ação.");

  const changeOwner = useMutation(
    () => axios.post(CLIENTS.BULK, { ids, ownerUserId }),
    {
      onSuccess: ({ data }) =>
        afterAction(
          `Responsável alterado em ${accounts(data.data?.updatedCount ?? total)}.`
        ),
      onError: fail,
    }
  );

  const applyTag = useMutation(
    async (action) => {
      const tagId =
        exactTag?.id ||
        (await axios.post(CRM.TAGS, { name: typedTag })).data.data.id;

      return axios.post(CRM.BULK_TAGS, { ids, tagId, action });
    },
    {
      onSuccess: ({ data }, action) =>
        afterAction(
          `Etiqueta ${action === "REMOVE" ? "tirada de" : "colocada em"} ${accounts(
            data.data?.changed ?? total
          )}.`
        ),
      onError: fail,
    }
  );

  const createTasks = useMutation(
    () =>
      axios.post(CRM.BULK_TASKS, {
        ids,
        title: taskTitle,
        dueDate: taskDueDate || null,
        priority: taskPriority,
        assignedToUserId: taskAssignee || undefined,
      }),
    {
      onSuccess: ({ data }) =>
        afterAction(`Tarefa criada em ${accounts(data.data?.created ?? total)}.`),
      onError: fail,
    }
  );

  if (!total) return null;

  const togglePanel = (id) =>
    setPanel((current) => (current === id ? null : id));

  return (
    <section
      data-testid="bulk-actions"
      className="rounded-2xl border border-pink-200 bg-pink-50/50 p-3 shadow-sm"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-slate-900">
          {accounts(total)} selecionada{total === 1 ? "" : "s"}
        </span>

        <button type="button" className={tabClass(panel === "task")} onClick={() => togglePanel("task")}>
          Criar tarefa
        </button>
        <button type="button" className={tabClass(panel === "tag")} onClick={() => togglePanel("tag")}>
          Etiqueta
        </button>
        {/* Redistribuir carteira é só do administrador, como na edição do cadastro. */}
        {isAdmin ? (
          <button type="button" className={tabClass(panel === "owner")} onClick={() => togglePanel("owner")}>
            Responsável
          </button>
        ) : null}

        <button
          type="button"
          onClick={onClear}
          className="ml-auto text-xs font-medium text-slate-500 hover:text-slate-800"
        >
          Limpar seleção
        </button>
      </div>

      {panel === "owner" ? (
        <form
          className="mt-3 flex flex-wrap items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            changeOwner.mutate();
          }}
        >
          <select
            aria-label="Novo responsável"
            value={ownerUserId}
            onChange={(event) => setOwnerUserId(event.target.value)}
            className={fieldClass}
          >
            <option value="">Sem responsável</option>
            {owners.map((owner) => (
              <option key={owner.id} value={owner.id}>
                {owner.name}
              </option>
            ))}
          </select>
          <button
            type="submit"
            disabled={changeOwner.isLoading}
            className="rounded-lg bg-pink-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
          >
            {changeOwner.isLoading ? "Aplicando..." : `Aplicar em ${accounts(total)}`}
          </button>
        </form>
      ) : null}

      {panel === "tag" ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input
            aria-label="Etiqueta"
            list="bulk-tags"
            value={tagName}
            maxLength={40}
            onChange={(event) => setTagName(event.target.value)}
            placeholder="Escolha ou escreva uma nova"
            className={fieldClass}
          />
          <datalist id="bulk-tags">
            {tags.map((tag) => (
              <option key={tag.id} value={tag.name} />
            ))}
          </datalist>
          <button
            type="button"
            disabled={!typedTag || applyTag.isLoading}
            onClick={() => applyTag.mutate("ADD")}
            className="rounded-lg bg-pink-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
          >
            {exactTag ? "Colocar" : "Criar e colocar"} em {accounts(total)}
          </button>
          {/* Só dá para tirar o que existe. */}
          {exactTag ? (
            <button
              type="button"
              disabled={applyTag.isLoading}
              onClick={() => applyTag.mutate("REMOVE")}
              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:opacity-40"
            >
              Tirar de {accounts(total)}
            </button>
          ) : null}
        </div>
      ) : null}

      {panel === "task" ? (
        <form
          className="mt-3 flex flex-wrap items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (taskTitle.trim()) createTasks.mutate();
          }}
        >
          <input
            aria-label="Título da tarefa"
            value={taskTitle}
            maxLength={180}
            onChange={(event) => setTaskTitle(event.target.value)}
            placeholder="Ex.: ligar para oferecer o upgrade"
            className={`${fieldClass} min-w-0 flex-1 basis-56`}
          />
          <input
            aria-label="Vencimento"
            type="date"
            value={taskDueDate}
            onChange={(event) => setTaskDueDate(event.target.value)}
            className={fieldClass}
          />
          <select
            aria-label="Prioridade"
            value={taskPriority}
            onChange={(event) => setTaskPriority(event.target.value)}
            className={fieldClass}
          >
            {PRIORITY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {isAdmin ? (
            <select
              aria-label="Responsável pela tarefa"
              value={taskAssignee}
              onChange={(event) => setTaskAssignee(event.target.value)}
              className={fieldClass}
            >
              <option value="">Dono de cada carteira (ou eu)</option>
              {owners.map((owner) => (
                <option key={owner.id} value={owner.id}>
                  {owner.name}
                </option>
              ))}
            </select>
          ) : null}
          <button
            type="submit"
            disabled={!taskTitle.trim() || createTasks.isLoading}
            className="rounded-lg bg-pink-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
          >
            {createTasks.isLoading ? "Criando..." : `Criar em ${accounts(total)}`}
          </button>
        </form>
      ) : null}
    </section>
  );
};

export default BulkActionsBar;
